import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from "@angular/core";
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from "@angular/router";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { DeviceDetectorService } from "ngx-device-detector";
import {
  NgcCookieConsentService,
  NgcStatusChangeEvent,
} from "ngx-cookieconsent";
import { filter, Subscription } from "rxjs";
import { CookieService } from "ngx-cookie-service";
import { MatIconModule } from "@angular/material/icon";
import { HeaderComponent } from "./Pages/common/header/header.component";
import { AlertComponent } from "./Components/alert/alert.component";
import { LandscapeService } from "./Services/platform";
import {
  EncryptDecryptService,
  EncryptionService,
} from "./Services/encryption";
import { environment } from "../environments/environment";
import { ApiConstants } from "./Services/api/ApiConstants";
import { NavigationService } from "./Services/navigation";
import { AuthService, TokenService } from "./Services/auth";
import { SecureStorageService } from "./Services/cache";
import { LoaderComponent } from "./Pages/common/loader/loader.component";

@Component({
  selector: "app-root",
  imports: [
    RouterOutlet,
    LoaderComponent,
    CommonModule,
    MatIconModule,
    HeaderComponent,
    AlertComponent,
  ],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit, OnDestroy {
  title = "bahamas";
  deviceInfo: any;
  latitude: number | null = null;
  longitude: number | null = null;
  getStatus!: Subscription;
  cookie_status: string = "";
  message_status: boolean = false;
  showRotateMessage = false;
  private cookieSubscriptionInitialized: boolean = false;
  private authSubscription?: Subscription;
  private _filterKeydownListener: ((e: KeyboardEvent) => void) | null = null;
  private isNavigating: boolean = false;
  showScrollTop: boolean = false;
  // internal: listener and flag stored on `this` to avoid TypeScript errors without major refactor

  constructor(
    private device: DeviceDetectorService,
    private ccService: NgcCookieConsentService,
    private cookieService: CookieService,
    private router: Router,
    private encryptDecrypt: EncryptDecryptService,
    private landscapeCheck: LandscapeService,
    private navigationService: NavigationService,
    private authService: AuthService,
    private secureStorage: SecureStorageService,
    private encryptionService: EncryptionService,
    private tokenService: TokenService,
    private activateRoute: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    // Consume any pending SSO payload stashed by main.ts BEFORE the Router
    // activates the first route. This must run synchronously in the constructor
    // so AuthGuard sees isLoggedIn() === true on the very first navigation.

    if (isPlatformBrowser(this.platformId)) {
      this.consumePendingSso();
    }
    this.checkOrientation();
  }

  /**
   * Reads `_pendingSso` (set by preBootstrapSso in main.ts), stores the token
   * + user data via TokenService, and marks the session as logged in via
   * AuthService. After this, AuthGuard.canActivate() will pass for the target
   * route (typically /dashboard/bin/{bin}).
   */
  private consumePendingSso(): void {
    try {
      const raw = sessionStorage.getItem("_pendingSso");
      if (!raw) return;
      sessionStorage.removeItem("_pendingSso");

      const data = JSON.parse(raw) || {};
      const token: string = data.token || "";
      const applicationid: string = data.applicationid || "";
      const bin: string = data.bin || "";
      const userId: string = data.userId || "";
      const payload: any = data.payload || {};
      if (!token || !userId) return;

      // JWT exp (seconds) -> ms
      const expSeconds = Number(payload.exp);
      if (Number.isFinite(expSeconds) && expSeconds > 0) {
        try {
          this.tokenService.setTokenExpiry(expSeconds * 1000, false);
        } catch {
          /* ignore */
        }
      }

      try {
        this.tokenService.setAccessToken(token, false);
      } catch {
        /* ignore */
      }

      const userObj: any = {
        id: userId,
        userId: userId,
        username: payload.userName || payload.username || "User",
        email: payload.email || null,
        role: payload.role || null,
        profile: payload.profile || null,
        BIN: bin || userId,
        binNumber: bin || userId,
        applicationId: applicationid || userId,
      };
      try {
        this.tokenService.setUserData(userObj, false);
      } catch {
        /* ignore */
      }
      try {
        this.tokenService.setUserId(userId, false);
      } catch {
        /* ignore */
      }

      try {
        this.authService.signIn(userId);
      } catch {
        /* ignore */
      }
      try {
        this.authService.markMfaVerified();
      } catch {
        /* ignore */
      }
      try {
        this.authService.refreshAuthState();
      } catch {
        /* ignore */
      }
    } catch (e) {
      console.warn("[SSO consume] failed", e);
    }
  }

  async ngOnInit(): Promise<void> {
    try {
      if (isPlatformBrowser(this.platformId)) {
        const searchParams = new URLSearchParams(window.location.search);
        console.log(
          "Checking for DMTT logout parameter in URL",
          searchParams.toString(),
        );
        const dmttLogout = searchParams.get("dmtt_logout");
        const userType = searchParams.get("userType");
        if (dmttLogout === "1") {
          console.log("DMTT logout detected, signing out...");
          document.cookie = "dmtt_logout=1; path=/; max-age=1000;secure"; // set a cookie to indicate DMTT logout for the next page load
          if (userType === "taxRep") {
            this.authService.signTaxRepClientOut();
            this.router.navigate(["/signup/dmtt"]);
          } else {
            this.authService.signDMTTOut();
          }
          return;
        }
        // else {
        //   this.authService.signOut();
        //   return;
        // }
      }
    } catch (e) {
      console.warn("Failed to check DMTT logout parameter", e);
    }

    // try {
    //   if (isPlatformBrowser(this.platformId)) {
    //     const searchParams = new URLSearchParams(window.location.search);
    //     const dmttLogout = searchParams.get("dmtt_logout");
    //     if (dmttLogout === "1") {
    //       console.log(
    //         "DMTT logout detected, clearing local session and redirecting to signup...",
    //       );
    //       this.authService.logoutLocal(false, "");
    //       setTimeout(() => {
    //         this.router.navigate(["/signup/dmtt"]);
    //       }, 300);
    //       return;
    //     }
    //   }
    // } catch (e) {
    //   console.warn("Failed to check DMTT logout parameter", e);
    // }

    await this.getZoomLevel();
    this.getDeviceInfo();
    this.getLocation();
    // this.authSubscription = this.authService.loggedIn$.subscribe(isLoggedIn => {
    //   if (!isLoggedIn) {
    //     this.ccService.destroy();
    //   }else{
    //     this.ccService.open();
    //   }
    // });
    // Load cookie consent status from persistent storage first (decrypt cookie value if present)
    const rawCookieConsent = this.cookieService.get("cookieconsent_status");
    const cookieConsentStatus = rawCookieConsent
      ? this.decryptCookieValue(rawCookieConsent)
      : "";
    const sessionConsentStatus = this.secureStorage.get<string>(
      "cookieconsent_status",
      true,
    ) as string | null;

    // console.log("Cookie consent status on init:", {
    //   fromCookie: cookieConsentStatus,
    //   fromSession: sessionConsentStatus
    // });

    this.cookie_status = cookieConsentStatus || sessionConsentStatus || "";

    // Migration: if there's a plain-text cookie (e.g. 'allow'/'deny') stored before encryption
    // replace it with an encrypted value so subsequent reads are encrypted.
    try {
      if (
        rawCookieConsent &&
        typeof rawCookieConsent === "string" &&
        rawCookieConsent.indexOf(":") === -1
      ) {
        // If it looks like an unencrypted status (allow/deny or simple string), encrypt & overwrite
        const simple = rawCookieConsent.trim();
        if (simple === "allow" || simple === "deny") {
          try {
            const enc = this.encryptionService.encrypt(simple);
            this.cookieService.set("cookieconsent_status", enc, 365);
            // console.log('Migrated plain cookieconsent_status to encrypted form');
          } catch (e) {
            console.warn(
              "Failed to migrate cookieconsent_status to encrypted form",
              e,
            );
          }
        }
      }
    } catch (e) {
      console.warn("cookieconsent_status migration check failed", e);
    }

    await this.cookieInitialisationMethods();
    this.checkOrientation();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        this.isNavigating = true;
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (isPlatformBrowser(this.platformId)) {
          try {
            window.scrollTo(0, 0);
          } catch (e) {}
        }

        try {
          const currentUrl =
            this.router && this.router.url
              ? String(this.router.url).toLowerCase()
              : "";
          const pathOnly = currentUrl.split(/[?#]/)[0];
          const preserve =
            pathOnly.startsWith("/account") ||
            pathOnly.startsWith("/dashboard/bin");
          if (!preserve) {
            try {
              this.secureStorage.remove("viewingDifferentUser", true);
            } catch (e) {
              /* ignore */
            }
          }
        } catch (e) {
          /* ignore */
        }

        this.isRouteChanged();
        setTimeout(() => {
          this.isNavigating = false;
        }, 100);
        // reset auto-scroll flag per navigation so each page can trigger behavior
        // (this as any)._hasAutoScrolledToBottom = false;
      });

    // Site-wide: prevent space key from triggering global shortcuts when typing
    // inside inputs marked with `.filter-input` (used for search/filter fields)
    if (isPlatformBrowser(this.platformId)) {
      // ensure initial scroll-to-top on first load
      try {
        window.scrollTo(0, 0);
      } catch (e) {}
      this._filterKeydownListener = (e: KeyboardEvent) => {
        try {
          const tgt = e.target as Element | null;
          if (!tgt) return;

          // Only consider input-like targets
          const tag = (tgt.tagName || "").toLowerCase();
          if (
            tag !== "input" &&
            tag !== "textarea" &&
            !(tgt as HTMLElement).isContentEditable
          )
            return;

          const el = tgt as HTMLElement;
          const cls = (el.className || "").toString();
          const placeholder = (el as HTMLInputElement).placeholder || "";
          const aria = el.getAttribute
            ? el.getAttribute("aria-label") || ""
            : "";
          const name = (el as HTMLInputElement).name || "";

          const ancestorFilter = !!el.closest(
            ".search-container, .search-bar-wrapper, .filter-input, [data-filter]",
          );
          const placeholderLower = placeholder.toLowerCase();
          const ariaLower = (aria || "").toLowerCase();
          const nameLower = (name || "").toLowerCase();

          const isFilter =
            cls.indexOf("search-input") !== -1 ||
            cls.indexOf("filter-input") !== -1 ||
            ancestorFilter ||
            placeholderLower.includes("search") ||
            placeholderLower.includes("filter") ||
            ariaLower.includes("search") ||
            nameLower.includes("search") ||
            nameLower.includes("filter");

          const tagToType = (el as HTMLInputElement).type
            ? ((el as HTMLInputElement).type || "").toLowerCase()
            : "";
          const disallowedInputTypes = [
            "button",
            "submit",
            "reset",
            "checkbox",
            "radio",
            "range",
            "color",
            "file",
            "image",
          ];
          const isDisabled = (el as HTMLInputElement).disabled || false;
          const isReadonly = (el as HTMLInputElement).readOnly || false;
          const isEditableInput =
            tag === "input" &&
            !disallowedInputTypes.includes(tagToType) &&
            !isDisabled &&
            !isReadonly;
          const isEditableTextarea =
            tag === "textarea" &&
            !(el as HTMLTextAreaElement).readOnly &&
            !(el as HTMLTextAreaElement).disabled;
          const isContentEditable = !!el.isContentEditable;
          const isEditableField =
            (isEditableInput || isEditableTextarea || isContentEditable) &&
            !el.hasAttribute("data-allow-space-scroll");

          if (
            (isFilter || isEditableField) &&
            (e.code === "Space" || e.key === " " || (e as any).keyCode === 32)
          ) {
            e.stopPropagation();
          }
        } catch (err) {
          // ignore errors in the global listener
        }
      };
      try {
        if (typeof window !== "undefined") {
          window.addEventListener("keydown", this._filterKeydownListener, true);
        }
      } catch (e) {
        /* ignore */
      }

      try {
        (this as any)._scrollListener = () => {
          try {
            const docEl = document.documentElement || document.body;
            const scrollTop =
              (window.pageYOffset || docEl.scrollTop) - (docEl.clientTop || 0);
            const scrollHeight = Math.max(
              document.documentElement.scrollHeight,
              document.body.scrollHeight,
            );
            const clientHeight =
              window.innerHeight || document.documentElement.clientHeight;
            const scrolledRatio =
              (scrollTop + clientHeight) / (scrollHeight || 1);
            this.showScrollTop = scrollTop > 0 && scrolledRatio >= 0.4;
            // if ((this as any)._hasAutoScrolledToBottom) return;
            // if (scrolledRatio >= 0.40) {
            //   (this as any)._hasAutoScrolledToBottom = true;
            //   try {
            //     window.scrollTo({ top: scrollHeight - clientHeight, behavior: 'smooth' });
            //   } catch (err) {
            //     try { window.scrollTo(0, scrollHeight); } catch (e) { }
            //   }
            // }
          } catch (err) {}
        };
        window.addEventListener("scroll", (this as any)._scrollListener, {
          passive: true,
        });
      } catch (e) {
        /* ignore */
      }
    }
  }

  @HostListener("window:resize")
  onResize() {
    this.message_status = false;
    this.checkOrientation();
  }

  @HostListener("window:popstate", ["$event"])
  onPopState(event: PopStateEvent): void {
    // console.log('Browser back button pressed', event);
    try {
      // If the user is logged in, cancel the back navigation by re-pushing the current URL
      if (
        this.authService &&
        this.authService.isLoggedIn &&
        this.authService.isLoggedIn()
      ) {
        try {
          if (isPlatformBrowser(this.platformId)) {
            const currentUrl =
              window.location.pathname +
              window.location.search +
              window.location.hash;
            history.pushState(null, document.title, currentUrl);
          }
        } catch (e) {
          try {
            history.replaceState(null, document.title);
          } catch (er) {}
        }
        // Optionally notify navigationService if it needs to handle UI state
        if (
          this.navigationService &&
          this.navigationService.handleBrowserBack
        ) {
          this.navigationService.handleBrowserBack();
        }
        return;
      }
    } catch (e) {
      console.warn("onPopState handler error", e);
    }
    this.navigationService.handleBrowserBack();
  }

  checkOrientation() {
    if (isPlatformBrowser(this.platformId)) {
      const isPortrait = this.landscapeCheck.isPortrait();
      if (isPortrait && !this.message_status) {
        this.message_status = true;
        this.showRotateMessage = true;
      }
    }
  }

  async cookieInitialisationMethods() {
    const rawCookieConsent = this.cookieService.get("cookieconsent_status");
    const cookieConsentStatus = rawCookieConsent
      ? this.decryptCookieValue(rawCookieConsent)
      : "";
    const sessionConsentStatus = this.secureStorage.get<string>(
      "cookieconsent_status",
      true,
    ) as string | null;

    // console.log('Cookie consent initialization check:', {
    //   cookieConsentStatus,
    //   sessionConsentStatus,
    //   cookie_status: this.cookie_status,
    //   ccService: this.ccService
    // });

    // Only show banner if user has never answered OR explicitly denied
    // Don't show if they already accepted (from cookie or session)
    if (
      isPlatformBrowser(this.platformId) &&
      cookieConsentStatus !== "allow" &&
      cookieConsentStatus !== "deny" &&
      sessionConsentStatus !== "allow" &&
      sessionConsentStatus !== "deny" &&
      this.cookie_status !== "allow"
    ) {
      // console.log('Opening cookie consent banner - first time visitor');
      try {
        this.ccService.open();
      } catch (error) {
        // console.log('Cookie service not available');
      }
    } else {
      // console.log('Cookie consent already handled, not showing banner');
    }
    if (!this.cookieSubscriptionInitialized) {
      this.getStatus = this.ccService.statusChange$.subscribe(
        async (event: NgcStatusChangeEvent) => {
          // console.log("Cookie consent status changed:", event);

          // Check if cookieconsent_status already exists (raw + decrypted)
          const existingConsentCookieRaw = this.cookieService.get(
            "cookieconsent_status",
          );
          const existingConsentCookie = existingConsentCookieRaw
            ? this.decryptCookieValue(existingConsentCookieRaw)
            : "";
          const existingSessionStatus = this.secureStorage.get<string>(
            "cookieconsent_status",
            true,
          ) as string | null;

          // Only update if the value is different or doesn't exist
          if (existingSessionStatus !== event.status) {
            this.secureStorage.set("cookieconsent_status", event.status, true);
            // console.log('Session storage updated with new consent status:', event.status);
          } else {
            // console.log('Session storage already has the same consent status, skipping update');
          }

          this.cookie_status = event.status;

          if (event.status === "allow") {
            // Determine whether we should (re)write the cookie encrypted
            const rawIsPlain =
              existingConsentCookieRaw &&
              existingConsentCookieRaw.indexOf(":") === -1;
            const shouldWriteEncrypted =
              !existingConsentCookieRaw ||
              existingConsentCookie !== "allow" ||
              rawIsPlain;
            if (shouldWriteEncrypted) {
              try {
                const enc = this.encryptionService.encrypt("allow");
                this.cookieService.set("cookieconsent_status", enc, 365);
                // console.log('Cookie consent status set to allow (encrypted)');
              } catch (e) {
                this.cookieService.set("cookieconsent_status", "allow", 365);
                // console.warn('EncryptionService failed, set cookie plain allow', e);
              }
            } else {
              // console.log('Cookie consent status already set to allow (encrypted), skipping duplicate');
            }

            // Check and set device cookies only if they don't exist
            await this.setCookie(
              "device browser",
              this.deviceInfo.browser + "," + this.deviceInfo.browser_version,
            );
            await this.setCookie(
              "device os",
              this.deviceInfo.os + "," + this.deviceInfo.os_version,
            );
            await this.setCookie("device type", this.deviceInfo.deviceType);

            if (this.latitude && this.longitude) {
              await this.setCookie(
                "geolocation",
                `latitude:${this.latitude}, longitude:${this.longitude}`,
              );
            }
            // console.log('User has accepted cookies, device information processed.');
          } else if (event.status === "deny") {
            // Only set denial status if it's not already set or if raw was plain
            const rawIsPlainDeny =
              existingConsentCookieRaw &&
              existingConsentCookieRaw.indexOf(":") === -1;
            const shouldWriteDeny =
              !existingConsentCookieRaw ||
              existingConsentCookie !== "deny" ||
              rawIsPlainDeny;
            if (shouldWriteDeny) {
              try {
                const enc = this.encryptionService.encrypt("deny");
                this.cookieService.set("cookieconsent_status", enc, 365);
                // console.log('Cookie consent status set to deny (encrypted)');
              } catch (e) {
                this.cookieService.set("cookieconsent_status", "deny", 365);
                // console.warn('EncryptionService failed, set cookie plain deny', e);
              }
            } else {
              // console.log('Cookie consent status already set to deny (encrypted), skipping duplicate');
            }

            // Clear all cookies and session storage when denied
            this.clearAllCookiesAndStorage();
            // console.log('User denied cookies. All cookies and session storage cleared.');
          }

          // Hide the banner completely after user makes a choice
          this.ccService.destroy();
        },
      );
      this.cookieSubscriptionInitialized = true;
    }
  }

  async setCookie(label: string, data: string) {
    let encryptedData = await this.encryptDecrypt.encryptData(data);
    this.cookieService.set(label, encryptedData, 1);
  }

  private decryptCookieValue(raw: string): string {
    // console.log('Decrypting cookie value',raw);
    try {
      const dec = this.encryptionService.decrypt(raw);
      // encryption service may return parsed JSON or string
      return typeof dec === "string" ? dec : JSON.stringify(dec);
    } catch (e) {
      // If decryption fails, fall back to raw value
      return raw;
    }
  }

  clearAllCookiesAndStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const cookies = [
          "device browser",
          "device os",
          "device type",
          "geolocation",
          "zoom-level",
          "cookieconsent_status",
        ];
        cookies.forEach((cookieName) => {
          this.cookieService.delete(cookieName);
          this.cookieService.delete(cookieName, "/"); // Also try with path
        });

        if (this.secureStorage.get("cookieconsent_status", true)) {
          this.secureStorage.remove("cookieconsent_status", true);
        }

        try {
          if (typeof window !== "undefined" && window.localStorage) {
            if (localStorage.getItem("cookieconsent_status")) {
              localStorage.removeItem("cookieconsent_status");
            }
          }
        } catch (e) {}

        // console.log('All cookies and storage cleared successfully');
      } catch (error) {
        console.error("Error clearing cookies and storage:", error);
      }
    }
  }

  async getZoomLevel() {
    let zoomLvl = this.cookieService.get("zoom-level") || "1";
    if (isPlatformBrowser(this.platformId) && typeof document != "undefined") {
      const wrapper = document.getElementById("zoom-wrapper");
      if (!wrapper) {
        // console.log("wrapper doesn't exist")
      } else {
        (wrapper as HTMLElement).style.zoom = zoomLvl as any;
        // console.log("zoom fetched from cookie", zoomLvl, (wrapper as HTMLElement).style.zoom)
      }
    }
  }

  scrollToTop(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (e) {
        try {
          window.scrollTo(0, 0);
        } catch (err) {}
      }
      this.showScrollTop = false;
    }
  }

  getDeviceInfo() {
    this.deviceInfo = this.device.getDeviceInfo();
    console.log("device info", this.deviceInfo);
    // console.log("device Type", this.deviceInfo.deviceType);
  }

  @HostListener("window:beforeunload", ["$event"])
  unloadHandler(event: Event) {
    this.handleBrowserReload();
  }

  // Commented for now as it is not going now
  // @HostListener('window:beforeunload', ['$event'])
  // beforeUnloadHandler(event: BeforeUnloadEvent): string | void {
  //   try {
  //     if (!isPlatformBrowser(this.platformId)) return;
  //     const isLoggedIn = this.authService && this.authService.isLoggedIn && this.authService.isLoggedIn();
  //     const isPerformingLogout = this.authService && typeof this.authService.isLogoutInProgress === 'function' ? this.authService.isLogoutInProgress() : false;
  //     if (isLoggedIn && !isPerformingLogout) {
  //       const msg = 'You are about to leave the OTB Dashboard. Click OK to logout.';
  //       try { event.preventDefault(); } catch (e) { /* ignore */ }
  //       (event as BeforeUnloadEvent).returnValue = msg;
  //       return msg;
  //     }
  //   } catch (e) {
  //     // nothing special
  //   }
  // }

  // @HostListener("window:unload", ["$event"])
  // onWindowUnload(event: Event) {
  //   try {
  //     if (!isPlatformBrowser(this.platformId)) return;
  //     if (this.isNavigating) {
  //       try {
  //         this.clearAllCookiesAndStorage();
  //       } catch (e) {}
  //       return;
  //     }

  //     const isLoggedIn =
  //       this.authService &&
  //       this.authService.isLoggedIn &&
  //       this.authService.isLoggedIn();
  //     const isPerformingLogout =
  //       this.authService &&
  //       typeof this.authService.isLogoutInProgress === "function"
  //         ? this.authService.isLogoutInProgress()
  //         : false;
  //     if (isLoggedIn && !isPerformingLogout) {
  //       try {
  //         const userId = this.authService.getCurrentUserId();
  //         const baseUrl = `${environment.apiUrl}/${ApiConstants.API_POST_Auth_Logout}`;
  //         const url = userId
  //           ? `${baseUrl}?userId=${encodeURIComponent(userId)}`
  //           : baseUrl;
  //         if (navigator && (navigator as any).sendBeacon) {
  //           try {
  //             (navigator as any).sendBeacon(url, "");
  //           } catch (e) {
  //             /* ignore */
  //           }
  //         } else {
  //           try {
  //             fetch(url, { method: "POST", keepalive: true, body: "" }).catch(
  //               () => {},
  //             );
  //           } catch (e) {
  //             /* ignore */
  //           }
  //         }
  //       } catch (e) {
  //         /* ignore */
  //       }
  //     }
  //   } catch (e) {
  //     /* ignore */
  //   }

  //   try {
  //     this.clearAllCookiesAndStorage();
  //   } catch (e) {
  //     /* ignore */
  //   }
  // }

  @HostListener("window:unload", ["$event"])
  onWindowUnload(event: Event) {
    try {
      if (!isPlatformBrowser(this.platformId)) return;

      // Check if this is a page refresh (F5/Cmd+R)
      const isPageRefresh = performance.navigation.type === 1;

      // Skip logout if navigating within app OR if page is being refreshed
      if (this.isNavigating || isPageRefresh) {
        try {
          this.clearAllCookiesAndStorage();
        } catch (e) {}
        return;
      }

      // Only logout on actual window close
      const isLoggedIn =
        this.authService &&
        this.authService.isLoggedIn &&
        this.authService.isLoggedIn();
      const isPerformingLogout =
        this.authService &&
        typeof this.authService.isLogoutInProgress === "function"
          ? this.authService.isLogoutInProgress()
          : false;
      if (isLoggedIn && !isPerformingLogout) {
        try {
          const userId = this.authService.getCurrentUserId();
          const baseUrl = `${environment.apiUrl}/${ApiConstants.API_POST_Auth_Logout}`;
          const url = userId
            ? `${baseUrl}?userId=${encodeURIComponent(userId)}`
            : baseUrl;
          if (navigator && (navigator as any).sendBeacon) {
            try {
              (navigator as any).sendBeacon(url, "");
            } catch (e) {
              /* ignore */
            }
          } else {
            try {
              fetch(url, { method: "POST", keepalive: true, body: "" }).catch(
                () => {},
              );
            } catch (e) {
              /* ignore */
            }
          }
        } catch (e) {
          /* ignore */
        }
      }
    } catch (e) {
      /* ignore */
    }

    try {
      this.clearAllCookiesAndStorage();
    } catch (e) {
      /* ignore */
    }
  }

  handleBrowserReload() {
    this.clearAllCookiesAndStorage();
    // console.log('Browser reload detected!');
  }

  @HostListener("window:orientationchange", ["$event"])
  onOrientationChange(event: Event) {
    event.preventDefault();
  }

  getLocation() {
    if (
      isPlatformBrowser(this.platformId) &&
      typeof navigator !== "undefined"
    ) {
      if ((navigator as any).geolocation) {
        (navigator as any).geolocation.getCurrentPosition(
          async (position: any) => {
            if (position) {
              this.latitude = position.coords.latitude;
              this.longitude = position.coords.longitude;
              // console.log("geolocation", this.latitude, this.longitude);
              try {
                await this.setCookie(
                  "geolocation",
                  `latitude:${this.latitude}, longitude:${this.longitude}`,
                );
              } catch (e) {}
            }
          },
          (error: any) => {
            console.error("Error getting location:", error);
          },
        );
      } else {
        console.error("Geolocation is not supported by this browser.");
      }
    }
  }

  isRouteChanged() {
    const rawCookieStatus = this.cookieService.get("cookieconsent_status");
    const cookieStatus = rawCookieStatus
      ? this.decryptCookieValue(rawCookieStatus)
      : "";
    const sessionStatus = this.secureStorage.get<string>(
      "cookieconsent_status",
      true,
    );
    // console.log('Route changed, checking cookie consent status');

    // console.log('Route change consent check:', {
    //   cookieStatus,
    //   sessionStatus
    // });

    // If user has already given consent (allow OR deny), don't show banner again
    if (
      cookieStatus === "allow" ||
      cookieStatus === "deny" ||
      sessionStatus === "allow" ||
      sessionStatus === "deny"
    ) {
      // console.log('User has already made cookie consent choice, not showing banner on route change');
      try {
        if (this.ccService) {
          this.ccService.destroy();
        }
      } catch (error) {
        // console.log('Cookie service already destroyed or not available');
      }
      return;
    }

    // Only minimize banner if user hasn't made any choice yet
    if (!cookieStatus && !sessionStatus) {
      // console.log('User hasnt made choice yet, minimizing banner for route change');
      this.minimizeCookieBanner();
    }
  }

  minimizeCookieBanner() {
    try {
      if (this.ccService) {
        this.ccService.close(true);
        // console.log('Cookie banner minimized');
      }
    } catch (error) {
      console.error("Error minimizing cookie banner:", error);
    }
  }

  ngOnDestroy() {
    if (this.getStatus) {
      this.getStatus.unsubscribe();
      this.cookieSubscriptionInitialized = false;
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    this.cookieService.delete("zoom-level");
    // cleanup delegated listener
    try {
      if (this._filterKeydownListener && isPlatformBrowser(this.platformId)) {
        if (typeof window !== "undefined") {
          window.removeEventListener(
            "keydown",
            this._filterKeydownListener,
            true,
          );
        }
        this._filterKeydownListener = null;
      }
    } catch (e) {
      /* ignore */
    }

    try {
      if ((this as any)._scrollListener && isPlatformBrowser(this.platformId)) {
        window.removeEventListener(
          "scroll",
          (this as any)._scrollListener as EventListenerOrEventListenerObject,
        );
        (this as any)._scrollListener = null;
      }
      // (this as any)._hasAutoScrolledToBottom = false;
    } catch (e) {
      /* ignore */
    }
  }
}
